import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { ShoppingBag, User, Menu, X } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";

const NAV_LINKS = [
  { label: "Shop", to: "/#shop" },
  { label: "Story", to: "/#story" },
  { label: "Contact", to: "/#contact" },
];

export default function Navbar() {
  const navigate = useNavigate();
  const location = useLocation();
  const { setOpen, count } = useCart();
  const { user } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);

  // Close the mobile menu whenever the route changes.
  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const go = (hash) => {
    setMenuOpen(false);
    navigate("/");
    setTimeout(() => {
      const el = document.querySelector(hash.replace("/", ""));
      if (el) el.scrollIntoView({ behavior: "smooth" });
      else window.scrollTo({ top: 0 });
    }, 60);
  };

  return (
    <nav
      data-testid="navbar"
      className="sticky top-0 z-50 bg-black/70 backdrop-blur-xl border-b border-white/10"
    >
      <div className="h-16 md:h-20 flex items-center justify-between px-5 md:px-12">
        <Link to="/" data-testid="logo-link" className="font-anton text-xl md:text-3xl tracking-tight uppercase text-white">
          Jersey<span className="text-primary">Blend</span>
        </Link>

        <div className="hidden md:flex items-center gap-10">
          {NAV_LINKS.map((l) => (
            <button
              key={l.label}
              data-testid={`nav-${l.label.toLowerCase()}`}
              onClick={() => go(l.to)}
              className="text-xs font-bold uppercase tracking-[0.25em] text-white/60 hover:text-primary transition-colors"
            >
              {l.label}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-2 md:gap-3">
          <Link
            to={user ? "/account" : "/login"}
            data-testid="nav-account-link"
            className="flex items-center gap-2 border border-white/20 text-white/80 hover:border-primary hover:text-primary p-2.5 md:px-4 md:py-2.5 text-xs font-bold uppercase tracking-[0.2em] transition-colors"
          >
            <User className="w-4 h-4" />
            <span className="hidden sm:inline">{user ? (user.name ? user.name.split(" ")[0] : "Account") : "Login"}</span>
          </Link>

          <button
            data-testid="nav-cart-button"
            onClick={() => setOpen(true)}
            className="group relative flex items-center gap-2 bg-primary text-black hover:bg-white p-2.5 md:px-5 md:py-2.5 text-xs font-bold uppercase tracking-[0.2em] transition-colors active:scale-95"
          >
            <ShoppingBag className="w-4 h-4" /> <span className="hidden sm:inline">Cart</span>
            {count > 0 && (
              <span
                data-testid="nav-cart-count"
                className="absolute -top-2 -right-2 flex h-5 min-w-5 items-center justify-center rounded-full bg-black text-white text-[10px] font-bold px-1.5"
              >
                {count}
              </span>
            )}
          </button>

          {/* Mobile menu toggle */}
          <button
            data-testid="nav-menu-toggle"
            onClick={() => setMenuOpen((v) => !v)}
            aria-label={menuOpen ? "Close menu" : "Open menu"}
            aria-expanded={menuOpen}
            className="md:hidden flex items-center justify-center p-2.5 border border-white/20 text-white/80 active:scale-95 transition-transform"
          >
            {menuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {/* Mobile menu panel */}
      {menuOpen && (
        <div
          data-testid="mobile-menu"
          className="md:hidden border-t border-white/10 bg-black/95 backdrop-blur-xl animate-in slide-in-from-top-2 fade-in duration-200"
        >
          <div className="flex flex-col px-5 py-4">
            {NAV_LINKS.map((l) => (
              <button
                key={l.label}
                data-testid={`mobile-nav-${l.label.toLowerCase()}`}
                onClick={() => go(l.to)}
                className="text-left text-sm font-bold uppercase tracking-[0.25em] text-white/80 hover:text-primary transition-colors py-4 border-b border-white/5 last:border-0"
              >
                {l.label}
              </button>
            ))}
            <Link
              to={user ? "/account" : "/login"}
              data-testid="mobile-nav-account"
              onClick={() => setMenuOpen(false)}
              className="text-left text-sm font-bold uppercase tracking-[0.25em] text-white/80 hover:text-primary transition-colors py-4"
            >
              {user ? "My Account" : "Login / Register"}
            </Link>
          </div>
        </div>
      )}
    </nav>
  );
}
